import * as React from 'react';
import {
  Divider, FormControl, FormControlLabel, FormGroup,
  Grid, Radio, RadioGroup, Switch,
  Typography
} from "@mui/material";
import {
  getPrefermentDefinition, ingredientTypeGrain, ingredientTypeInclusion, ingredientTypeLiquid,
  isYeastPreferment,
  LEVAIN_PCT_TYPE_TOTAL_WT,
  noPreferment
} from "../shared/Types";
import {PrefermentTypeSelector} from "../shared/PrefermentTypeSelector";
import {
  getMaxYeastRatioId,
  getYeastPercentFromId,
  getYeastRatioSelectLabel
} from "../shared/Yeast";
import {toFixedDecimal} from "../utils/NumberUtils";
import {useState} from "react";
import {
  helpFermVolTemp
} from "../help/PopupHelpContent";
import {ContextualHelpDialog} from "../help/ContextualHelpDialog";
import {
  fermTemps,
  getLevainTime,
  getYeastTime,
  levainVolumes
} from "../shared/FermentationTiming";
import {FermentationTimingControl} from "../shared/FermentationTimingControl";
import {IngredientGroupForm} from "../shared/IngredientGroupForm";
import {FermentationTimingSettingsDialog} from "../shared/FermentationTimingSettingsDialog";
import {FormulaSliderEditor} from "../shared/FormulaSliderEditor";
import {
  prefHydroMax,
  prefHydroMin,
  prefHydroSliderMax,
  prefHydroSliderMin,
  prefStarterMax,
  prefStarterMin,
  prefStarterSliderMax,
  prefStarterSliderMin, prefYeastMin, prefYeastSliderMin
} from "./DoughFormDefaults";
import {IngredientCollectionForm} from "../shared/IngredientCollectionForm";

function PrefermentForm(props) {
  const {formData, setFormData, includePrefermentValue, includePrefermentChange, notifyPrefermentTypeChange,
    updateIngredientList, componentId} = props
  const [openFermTimingDialog, setOpenFermTimingDialog] = useState(false)
  const [openContextualHelpDialog, setOpenContextualHelpDialog] = useState(false)
  const [contextualHelpContent, setContextualHelpContent] = useState({title: undefined, content: undefined})

  function openHelp(title, content) {
    setContextualHelpContent({title: title, content: content})
    setOpenContextualHelpDialog(true)
  }

  function onCloseHelp() {
    setOpenContextualHelpDialog(false)
    setContextualHelpContent({title: undefined, content: undefined})
  }

  function updateFlourData(updatedFlourData) {
    setFormData({
      ...formData,
      flour: updatedFlourData
    })
  }

  function updateLiquidData(updatedLiquidData) {
    setFormData({
      ...formData,
      liquid: updatedLiquidData
    })
  }

  function updateMixinData(mixinData) {
    setFormData({
      ...formData,
      mixin: mixinData
    })
  }

  function onYeastRatioChange(evt) {
    let ratioId = parseInt(evt.target.value)
    if (isNaN(ratioId)) {
      ratioId = 0
    }
    setFormData({
      ...formData,
      yeastRatioId: ratioId,
      yeastPct: getYeastPercentFromId(ratioId)
    })
  }

  function onTimingParamsChange(evt) {
    setFormData({
      ...formData,
      [evt.target.name]: evt.target.value
    })
  }

  function onAddBufferChange(evt) {
    setFormData({
      ...formData,
      addBuffer: evt.target.checked
    })
  }

  function onPrefermentTypeChange(value) {
    const prefermentDef = getPrefermentDefinition(value)
    setFormData({...formData,
                 prefermentType: value,
                 prefermentPffPctType: prefermentDef.prefermentPffPctType,
                 hydrationPct: prefermentDef.defaultHydrationPct,
                 yeastRatioId: prefermentDef.yeastRatioId,
                 yeastPct: 0,
                 starterPct: prefermentDef.starterPct,
                 starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
                 starterHydrationPct: 100
    })
    notifyPrefermentTypeChange(value)
  }

  function getYeastSliderHeading() {
    if (formData.yeastRatioId > 0) {
      return `${toFixedDecimal(getYeastPercentFromId(formData.yeastRatioId), 2)}%`
    }
    return 'None'
  }

  function renderPrefermentLeavening() {
    return (
      <div>
        {!isYeastPreferment(formData.prefermentType) ? (
          <FormulaSliderEditor displayLabel={`Starter Seed%`} fieldName={'starterPct'}
               fieldValue={formData.starterPct} fieldContainer={formData} auxFieldName={'starterHydrationPct'}
               auxFieldValue={formData.starterHydrationPct} auxDisplayLabel={`Seed H%`}
               updateFn={setFormData} sliderColor={formData.starterPct > 0 ? "primary" : "attention"}
               minVal={prefStarterMin} maxVal={prefStarterMax}
               sliderMinVal={prefStarterSliderMin} sliderMaxVal={prefStarterSliderMax} sliderStep={1}/>
        ) : null}
        {isYeastPreferment(formData.prefermentType) ? (
          <FormulaSliderEditor displayLabel={`Dry Yeast: ${getYeastSliderHeading()}`} fieldName={'yeastRatioId'}
                               fieldValue={formData.yeastRatioId} fieldContainer={formData} onChangeFn={onYeastRatioChange}
                               updateFn={setFormData} sliderColor={formData.yeastRatioId > 0 ? "primary" : "attention"}
                               valueDisplayFn={getYeastRatioSelectLabel}
                               minVal={prefYeastMin} maxVal={getMaxYeastRatioId()}
                               sliderMinVal={prefYeastSliderMin} sliderMaxVal={getMaxYeastRatioId()} sliderStep={1}
                               disableNumericEdit={true}/>
        ) : null}
        <FermentationTimingControl headingLabel={'Pre Ferm'} fermPctValueArr={levainVolumes} fermTempValueArr={fermTemps}
                                   timeDisplayValue={prefermentLevainTimeFormatted} timeDisplayWarning={() => undefined}
                                   fermPctValue={formData.levainFermPct} fermPctName={'levainFermPct'} fermPctLabel={'Pre Ferm Goal'}
                                   fermStateLabel={'peak'} fermTempValue={formData.levainFermTempF} fermTempName={'levainFermTempF'} fermTempLabel={'Pre Ferm Temp'}
                                   onChange={onTimingParamsChange} onHelpClick={() => openHelp(undefined, helpFermVolTemp)}/>
      </div>
    )
  }


  function prefermentLevainTimeFormatted() {
    if (formData.starterPct > 0) {
      return `~${getLevainTime(formData.levainFermTempF, formData.levainFermPct, formData.starterPct, formData.hydrationPct)}`
    } else if (formData.yeastRatioId > 0) {
      return `~${getYeastTime(formData.levainFermTempF, formData.levainFermPct, formData.yeastRatioId, formData.hydrationPct)}`
    } else {
      return 'n/a'
    }
  }

  return (
    <div>
      <Grid item container direction={'column'} marginTop={-2}>
        <FormControl>
          <RadioGroup aria-labelledby="prefermentStyle" row
                      defaultValue={'N'}
                      sx={{marginLeft: 0}}
                      value={includePrefermentValue ? 'Y' : 'N'}
                      onChange={(evt) => includePrefermentChange(evt.target.value === 'Y')}>
            <FormControlLabel sx={{marginLeft: 0.15, marginRight: 0}} value={'N'}
                              control={<Radio size={'small'} color={'primary'}/>}
                              label={<Typography sx={{fontSize: 14}}>Simple ingredient of Main</Typography>}/>
            <FormControlLabel sx={{marginLeft: 0, marginRight: 0}} value={'Y'}
                              control={<Radio size={'small'} color={'primary'}/>}
                              label={<Typography sx={{fontSize: 14}}>Detailed</Typography>}/>
          </RadioGroup>
        </FormControl>

      </Grid>
      {includePrefermentValue ? (
      <Grid item container direction={'column'}>
        <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
          <Typography sx={{fontSize: 12, fontWeight: 350}}>Detailed Preferment</Typography>
        </Divider>
        <Grid item container direction={'column'} marginTop={2}>
          <Grid item container direction={'row'} marginBottom={1.5}>
            <Grid item >
              <PrefermentTypeSelector id={'weight-pf'} prefermentType={formData.prefermentType} onValueChange={onPrefermentTypeChange} excludeTypes={[noPreferment]}/>
            </Grid>
            <Grid item marginLeft={2}>
              <FormGroup row>
                <FormControlLabel control={<Switch name={'addBuffer'} checked={formData.addBuffer}
                                                   onChange={onAddBufferChange}/>}
                                  label={<Typography variant={'subtitle2'}>+Buffer</Typography>}/>
              </FormGroup>
            </Grid>
          </Grid>
          <FormulaSliderEditor displayLabel={'Preferment Hydration %'} fieldName={'hydrationPct'} fieldValue={formData.hydrationPct} fieldContainer={formData}
                               updateFn={setFormData} sliderColor={'water'} minVal={prefHydroMin} maxVal={prefHydroMax}
                               sliderMinVal={prefHydroSliderMin} sliderMaxVal={prefHydroSliderMax} sliderStep={1}/>
        </Grid>
        <div>
          <Divider textAlign={'right'} sx={{width: '90%'}}>
            <Typography sx={{fontSize: 12, fontWeight: 350}} gutterBottom>Leavening</Typography>
          </Divider>
          {renderPrefermentLeavening()}
          <Divider textAlign={'right'} sx={{marginTop: 1, width: '90%'}}>
            <Typography sx={{fontSize: 12, fontWeight: 350}}>Flour</Typography>
          </Divider>
          <Grid item container direction={'column'} alignItems={'flex-start'} >
            <IngredientGroupForm ingredientData={formData.flour} setIngredientData={updateFlourData} balanceGoal={100} idField={'id'} nameField={'name'}
                       unitField={'percent'} groupName={'Flour'} minGroupSize={1} disabled={false} ingredientType={ingredientTypeGrain}
                       updateIngredientList={updateIngredientList} componentId={componentId}/>
          </Grid>
        </div>
        <div>
          <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
            <Typography sx={{fontSize: 12, fontWeight: 350}}>Liquid</Typography>
          </Divider>
          <Grid item container direction={'column'} alignItems={'flex-start'} >
            <IngredientGroupForm ingredientData={formData.liquid} setIngredientData={updateLiquidData} balanceGoal={100} idField={'id'}
                        nameField={'name'} unitField={'percent'} minGroupSize={1} groupName={'Liquid'} disabled={false} ingredientType={ingredientTypeLiquid}
                                 updateIngredientList={updateIngredientList} componentId={componentId}/>
          </Grid>
          <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
            <Typography sx={{fontSize: 12, fontWeight: 350}}>Inclusions</Typography>
          </Divider>
          <Grid item container direction={'column'} alignItems={'flex-start'} >
            <IngredientCollectionForm ingredientData={formData.mixin} setIngredientData={updateMixinData} idField={'id'}
                                      nameField={'name'} unitField={'percent'} collectionName={'Inclusion'} ingredientType={ingredientTypeInclusion}
                                      updateIngredientList={updateIngredientList} componentId={componentId}/>
          </Grid>
        </div>

        <ContextualHelpDialog isOpen={openContextualHelpDialog} close={onCloseHelp}
                              title={contextualHelpContent.title} content={contextualHelpContent.content}/>
        <FermentationTimingSettingsDialog formData={formData} setFormData={setFormData}
                                          open={openFermTimingDialog} close={() => setOpenFermTimingDialog(false)}/>
      </Grid>) : null}
  </div>
  )
}

export { PrefermentForm }